import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Button, P, F } from 'components/Ui';
import moment from 'moment';
import { Mobile, Default } from 'components/Media';
import { apiCall } from 'utils/swaggerClient';
import { createDownloadLink } from 'utils/fileDownload';
import {
  selectBillingStatements,
  selectBillingStatementsIsFetching,
} from 'containers/PrimoAccount/selectors';
import { loadRecurringOrder } from 'containers/PrimoAccount/actions';
import { showErrorSnackbar } from 'components/Notifier/actions';
import { CloseExpandIcon } from 'components/Ui/CustomIcons';
import ExpandIcon from 'components/Ui/CustomIcons/ExpandIcon';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import IconButton from '@material-ui/core/IconButton';
import Loader from 'components/Loader';
import styles from './styles.scss';

function PastOrdersList({
  billingStatements,
  _currentAddress,
  defaultServiceLocationId,
  isFetchingStatements,
  serviceLocations,
  ...props
}) {
  const printOrderInvoice = async (statementDate, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await apiCall({
        operationId: 'ServiceLocations.getServiceLocationStatementsInvoice',
        parameters: {
          serviceLocationId: defaultServiceLocationId,
          statementDate,
        },
        fullResponse: true,
      });

      createDownloadLink({
        data,
        fileName: `Order_Invoice_${statementDate}.pdf`,
      });
    } catch (error) {
      props.showErrorSnackbar('Error generating invoice');
    }
  };

  const getDeliveryTicket = async (transaction, e) => {
    e.preventDefault();
    e.stopPropagation();
    const { ticketDate: deliveryDate, serviceLocationId } = transaction;
    try {
      const { data } = await apiCall({
        operationId: 'ServiceLocations.getServiceLocationDeliveryTicket',
        parameters: { serviceLocationId, deliveryDate },
        fullResponse: true,
      });

      createDownloadLink({
        data,
        fileName: `Delivery_ticket_${deliveryDate}.pdf`,
      });
    } catch (err) {
      props.showErrorSnackbar('Error generating ticket');
    }
  };

  const filterBillingStatementsByDates = (order) => {
    const { showOnlyFilter } = props;

    const isFilterAYear = String(showOnlyFilter).length === 4;
    if (isFilterAYear) {
      return moment(order.endDate).year() === +showOnlyFilter;
    }
    return moment(showOnlyFilter).isSameOrBefore(moment(order.endDate));
  };

  const renderOrderItem = (order, i) => (
    <ExpansionPanel key={order.statementDate} className={styles.orderList}>
      <ExpansionPanelSummary
        data-tracking={`Invoiced ${order.statementDate.replace('/-/g', '/')}`}
        className={styles.date}
      >
        <div className={styles.heading}>
          <h2>
            <F>Invoiced</F> {order.statementDate.replace('/-/g', '/')}
          </h2>
          <Mobile>
            <Button
              link
              className={styles.printInvoice}
              onClick={(e) => printOrderInvoice(order.statementDate, e)}
              text="View invoice"
            />
          </Mobile>
        </div>
        <Default>
          <Button
            className={styles.printInvoice}
            onClick={(e) => printOrderInvoice(order.statementDate, e)}
            text="View/Print invoice"
          />
          <IconButton
            className={styles.iconExpanded}
            onClick={() => {
              // eslint-disable-next-line no-undef,no-unused-vars

              const currExpanded = billingStatements[i];

              const billingStatements1 = billingStatements.map((elm, idx) => {
                if (i === idx) {
                  elm.expanded = !currExpanded;
                }
                return elm;
              });
              return { billingStatements1 };
            }}
            aria-expanded={order.expanded}
            aria-label="show more"
          >
            {order.expanded ? <CloseExpandIcon /> : <ExpandIcon />}
          </IconButton>
        </Default>
      </ExpansionPanelSummary>

      <ExpansionPanelDetails className={styles.ticketDisplay}>
        <P
          style={{
            textTransform: 'uppercase',
            fontSize: '11px',
            fontWeight: 'bold',
          }}
        >
          Delivery Address:
        </P>

        {order.tickets.map((ticket) => {
          const serviceLocation = serviceLocations.find(
            (location) => location.id === ticket.serviceLocationId.toString(),
          );
          const {
            address1 = '',
            address2 = '',
            city = '',
            postalCode = '',
            stateOrProvinceCode = '',
          } = serviceLocation || {};

          return (
            <div key={`${ticket.serviceLocationId}-${ticket.ticketDate}`}>
              <span>{ticket.ticketDate}</span>
              <span
                className={styles.addressDisplay}
              >{`${address1.toLowerCase()} ${address2.toLowerCase()}, ${city.toLowerCase()}, ${stateOrProvinceCode} ${postalCode}`}</span>
              <Button
                className={styles.printInvoice}
                onClick={(e) => getDeliveryTicket(ticket, e)}
                text="View/Print delivery ticket"
                link
              />
            </div>
          );
        })}
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );

  const billingStatementsDisplay = billingStatements || [];

  return (
    <Loader loading={isFetchingStatements} local>
      {billingStatementsDisplay
        .filter(filterBillingStatementsByDates)
        .map(renderOrderItem)}
    </Loader>
  );
}

PastOrdersList.propTypes = {
  showOnlyFilter: PropTypes.string.isRequired,
  serviceLocationId: PropTypes.string,
  currentAddress: PropTypes.object,
  showErrorSnackbar: PropTypes.func.isRequired,
  loadRecurringOrder: PropTypes.func,
  billingStatements: PropTypes.arrayOf(PropTypes.object),
  defaultServiceLocationId: PropTypes.string,
  serviceLocations: PropTypes.array,
};

PastOrdersList.defaultProps = {
  defaultServiceLocationId: null,
  isFetchingStatements: false,
  serviceLocations: [],
};

const mapStateToProps = createStructuredSelector({
  billingStatements: selectBillingStatements(),
  isFetchingStatements: selectBillingStatementsIsFetching(),
});
const mapDispatchToProps = {
  loadRecurringOrder,
  showErrorSnackbar,
};

export default connect(mapStateToProps, mapDispatchToProps)(PastOrdersList);
